import {
  EEX_QUOTE_SCHEMA_VERSION,
  eexQuoteMonthHours,
  makeEexQuoteRows,
  type EexQuoteRowComputed,
  type EexQuoteRowInput,
  type EexQuoteSettings,
} from "@chro/shared";
import { z } from "zod";
import {
  EUR_RON_PAIR,
  type QuoteFxCurveSnapshot,
} from "../../../lib/eex-fx";

export type QuoteWorkspaceKind = "quote" | "preset";
export type QuoteVisibility = "private" | "company";
export type QuoteDensity = "compact" | "comfortable";

export interface QuoteMetadata {
  title: string;
  counterparty: string;
  notes: string;
  asOf: string;
  marketDataSource: string;
  inputsConfirmed: boolean;
  validityMinutes: number;
}

export interface QuoteLayout {
  columnOrder: QuoteColumnId[];
  hiddenColumns: QuoteColumnId[];
  columnWidths: Partial<Record<QuoteColumnId, number>>;
  density: QuoteDensity;
  showShapePnl: boolean;
}

export interface QuoteComponent {
  id: string;
  name: string;
  months: string[];
  mw: number;
}

interface QuoteSnapshotCore {
  fxCurve: QuoteFxCurveSnapshot | null;
  metadata: QuoteMetadata;
  settings: EexQuoteSettings;
  rows: EexQuoteRowInput[];
  components: QuoteComponent[];
  layout: QuoteLayout;
}

export interface QuoteSnapshotV1 extends QuoteSnapshotCore {
  schemaVersion: typeof EEX_QUOTE_SCHEMA_VERSION;
}

export type QuoteMarketPolicy = "bid" | "ask" | "mid" | "last";
export const EEX_QUOTE_MARKET_SOURCE = "EEX DataSource REST API (cached)" as const;

export interface QuoteMarketMark {
  acquisitionScopeId: string;
  priceSourceScopeId?: string;
  marketInstrumentId: string;
  providerInstrumentKey: string;
  instrumentIsin: string | null;
  bid: number | null;
  ask: number | null;
  mid: number | null;
  last: number | null;
  bidVolume: number | null;
  askVolume: number | null;
  lastVolume: number | null;
  chosen: number;
  marketTimestamp: string;
  receivedAt: string;
  freshnessAtApplication: "fresh";
  coverageAtApplication: "quoted";
}

export interface QuoteMarketApplicationRow {
  month: string;
  hu: QuoteMarketMark;
  ro: QuoteMarketMark | null;
  observedBasis: number | null;
  basisApplied: boolean;
  huPriceManualOverride: boolean;
  quotedSpreadManualOverride: boolean;
}

export interface QuoteMarketApplication {
  companyId: string;
  snapshotId: string;
  provider: "EEX";
  source: typeof EEX_QUOTE_MARKET_SOURCE;
  asOf: string;
  selectedPolicy: QuoteMarketPolicy;
  basisApplied: boolean;
  appliedAt: string;
  signatureVersion: 1;
  signature: string;
  rows: QuoteMarketApplicationRow[];
}

export interface QuoteSnapshotV2 extends QuoteSnapshotCore {
  schemaVersion: 2;
  marketSnapshot: QuoteMarketApplication;
}

export type QuoteSnapshot = QuoteSnapshotV1 | QuoteSnapshotV2;

export interface QuoteWorkspaceSummary {
  id: string;
  kind: QuoteWorkspaceKind;
  name: string;
  visibility: QuoteVisibility;
  version: number;
  ownerUserId: string;
  ownerLabel?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteWorkspaceRecord extends QuoteWorkspaceSummary {
  snapshot: QuoteSnapshot;
}

export type QuoteColumnId =
  | "month"
  | "hours"
  | "mw"
  | "mwh"
  | "huPrice"
  | "quotedSpread"
  | "risk"
  | "spread"
  | "roEur"
  | "fx"
  | "roRon"
  | "buyRon"
  | "sellRon"
  | "buyEur"
  | "sellEur"
  | "shapePnlRon";

export type EditableQuoteRowKey = "mw" | "huPrice" | "quotedSpread" | "risk" | "fx";

export interface QuoteColumnSpec {
  id: QuoteColumnId;
  title: string;
  shortTitle: string;
  group: string;
  description: string;
  width: number;
  accessor: keyof EexQuoteRowComputed;
  editableKey?: EditableQuoteRowKey;
  decimals: "price" | "fx" | number;
  tone: "label" | "input" | "derived" | "physical" | "analysis";
}

export const QUOTE_COLUMNS: readonly QuoteColumnSpec[] = [
  {
    id: "month",
    title: "Delivery month",
    shortTitle: "Month",
    group: "Delivery",
    description: "EEX delivery month",
    width: 96,
    accessor: "month",
    decimals: 0,
    tone: "label",
  },
  {
    id: "hours",
    title: "Delivery hours",
    shortTitle: "h",
    group: "Delivery",
    description: "Actual CET/CEST hours, including DST",
    width: 72,
    accessor: "hours",
    decimals: 0,
    tone: "derived",
  },
  {
    id: "mw",
    title: "Volume MW",
    shortTitle: "MW",
    group: "Delivery",
    description: "Monthly quoted capacity",
    width: 82,
    accessor: "mw",
    editableKey: "mw",
    decimals: 2,
    tone: "input",
  },
  {
    id: "mwh",
    title: "Energy MWh",
    shortTitle: "MWh",
    group: "Delivery",
    description: "Delivery hours × MW",
    width: 104,
    accessor: "mwh",
    decimals: 0,
    tone: "derived",
  },
  {
    id: "huPrice",
    title: "HU forward EUR/MWh",
    shortTitle: "HU",
    group: "Financial EUR",
    description: "HU baseload forward market input",
    width: 104,
    accessor: "huPrice",
    editableKey: "huPrice",
    decimals: "price",
    tone: "input",
  },
  {
    id: "quotedSpread",
    title: "Quoted basis EUR/MWh",
    shortTitle: "Quoted",
    group: "Basis",
    description: "Trader's quoted RO–HU basis",
    width: 100,
    accessor: "quotedSpread",
    editableKey: "quotedSpread",
    decimals: "price",
    tone: "input",
  },
  {
    id: "risk",
    title: "Risk EUR/MWh",
    shortTitle: "Risk",
    group: "Basis",
    description: "Risk adjustment added to quoted basis",
    width: 88,
    accessor: "risk",
    editableKey: "risk",
    decimals: "price",
    tone: "input",
  },
  {
    id: "spread",
    title: "Effective spread EUR/MWh",
    shortTitle: "Spread",
    group: "Financial EUR",
    description: "Quoted basis + risk",
    width: 100,
    accessor: "spread",
    decimals: "price",
    tone: "derived",
  },
  {
    id: "roEur",
    title: "RO financial EUR/MWh",
    shortTitle: "RO EUR",
    group: "Financial EUR",
    description: "HU forward + effective spread",
    width: 108,
    accessor: "roEur",
    decimals: "price",
    tone: "derived",
  },
  {
    id: "fx",
    title: "Forward FX EUR/RON",
    shortTitle: "FX",
    group: "Financial RON",
    description: "RON per EUR, keyed by delivery month",
    width: 100,
    accessor: "fx",
    editableKey: "fx",
    decimals: "fx",
    tone: "input",
  },
  {
    id: "roRon",
    title: "RO financial RON/MWh",
    shortTitle: "RO RON",
    group: "Financial RON",
    description: "RO EUR × forward FX",
    width: 112,
    accessor: "roRon",
    decimals: "price",
    tone: "derived",
  },
  {
    id: "buyRon",
    title: "Physical buy RON/MWh",
    shortTitle: "Buy RON",
    group: "Physical RON",
    description: "RO financial RON + configurable buy adder",
    width: 112,
    accessor: "buyRon",
    decimals: "price",
    tone: "physical",
  },
  {
    id: "sellRon",
    title: "Physical sell RON/MWh",
    shortTitle: "Sell RON",
    group: "Physical RON",
    description: "Physical buy RON + configurable sell width",
    width: 112,
    accessor: "sellRon",
    decimals: "price",
    tone: "physical",
  },
  {
    id: "buyEur",
    title: "Physical buy EUR/MWh",
    shortTitle: "Buy EUR",
    group: "Physical EUR",
    description: "RO financial EUR + configurable buy adder",
    width: 108,
    accessor: "buyEur",
    decimals: "price",
    tone: "physical",
  },
  {
    id: "sellEur",
    title: "Physical sell EUR/MWh",
    shortTitle: "Sell EUR",
    group: "Physical EUR",
    description: "Physical buy EUR + configurable sell width",
    width: 108,
    accessor: "sellEur",
    decimals: "price",
    tone: "physical",
  },
  {
    id: "shapePnlRon",
    title: "Shape P&L RON",
    shortTitle: "Shape P&L",
    group: "Analysis",
    description: "(flat weighted sell – monthly sell) × MWh",
    width: 132,
    accessor: "shapePnlRon",
    decimals: 0,
    tone: "analysis",
  },
] as const;

export const QUOTE_COLUMN_BY_ID = new Map(QUOTE_COLUMNS.map((column) => [column.id, column]));

export const DEFAULT_QUOTE_SETTINGS: EexQuoteSettings = {
  ronBuyAdder: 10,
  ronSellAdder: 10,
  eurBuyAdder: 2,
  eurSellAdder: 2,
  defaultRisk: 2,
  priceDecimals: 2,
  fxDecimals: 4,
};

export const DEFAULT_QUOTE_LAYOUT: QuoteLayout = {
  columnOrder: QUOTE_COLUMNS.map((column) => column.id),
  hiddenColumns: [],
  columnWidths: {},
  density: "compact",
  showShapePnl: true,
};

const REFERENCE_MARKET_INPUTS: ReadonlyArray<
  readonly [month: string, huPrice: number, quotedSpread: number, mw: number]
> = [
  ["2026-08", 139.75, -5, 0],
  ["2026-09", 149.5, -5, 0],
  ["2026-10", 155.6, -6.25, 0],
  ["2026-11", 155.6, -6.25, 0],
  ["2026-12", 155.6, -6.25, 0],
  ["2027-01", 155.75, -5.75, 0],
  ["2027-02", 153.75, -5.75, 0],
  ["2027-03", 128, -5.75, 30],
  ["2027-04", 98, -5.75, 30],
  ["2027-05", 98, -5.75, 30],
  ["2027-06", 98, -5.75, 30],
  ["2027-07", 113.85, -5.75, 30],
  ["2027-08", 113.85, -5.75, 30],
  ["2027-09", 113.85, -5.75, 30],
  ["2027-10", 126.25, -5.75, 30],
  ["2027-11", 126.25, -5.75, 30],
  ["2027-12", 126.25, -5.75, 30],
  ["2028-01", 126.9166666667, -3, 30],
  ["2028-02", 124.9166666667, -3, 30],
  ["2028-03", 99.1666666667, -3, 0],
  ["2028-04", 78.0166666667, -1, 0],
  ["2028-05", 78.0166666667, -1, 0],
  ["2028-06", 78.0166666667, -1, 0],
  ["2028-07", 93.8666666667, -1.75, 0],
  ["2028-08", 93.8666666667, -1.75, 0],
  ["2028-09", 93.8666666667, -1.75, 0],
  ["2028-10", 106.2666666667, -1.75, 0],
  ["2028-11", 106.2666666667, -1.75, 0],
  ["2028-12", 106.2666666667, -1.75, 0],
];

export function createDefaultQuoteSnapshot(fxCurve: QuoteFxCurveSnapshot | null = null): QuoteSnapshot {
  const generated = makeEexQuoteRows("2026-08", "2028-12", DEFAULT_QUOTE_SETTINGS);
  const byMonth = new Map(REFERENCE_MARKET_INPUTS.map((point) => [point[0], point]));
  const fxByMonth = new Map(fxCurve?.points.map((point) => [point.deliveryMonth, point.mid]) ?? []);
  const rows = generated.map((row) => {
    const point = byMonth.get(row.month);
    return point
      ? {
          ...row,
          huPrice: point[1],
          quotedSpread: point[2],
          risk: DEFAULT_QUOTE_SETTINGS.defaultRisk,
          fx: fxByMonth.get(row.month) ?? null,
          mw: point[3],
        }
      : { ...row, fx: fxByMonth.get(row.month) ?? null };
  });
  return {
    schemaVersion: EEX_QUOTE_SCHEMA_VERSION,
    fxCurve,
    metadata: {
      title: "Mar-27 – Feb-28 physical strip",
      counterparty: "",
      notes: "",
      asOf: "2026-07-15",
      marketDataSource: "Extras (4) reference workbook",
      inputsConfirmed: false,
      validityMinutes: 30,
    },
    settings: { ...DEFAULT_QUOTE_SETTINGS },
    rows,
    components: [
      {
        id: "reference-cal-27",
        name: "Cal-27",
        months: generated.filter((row) => row.month.startsWith("2027-")).map((row) => row.month),
        mw: 1,
      },
      {
        id: "reference-cal-28",
        name: "Cal-28",
        months: generated.filter((row) => row.month.startsWith("2028-")).map((row) => row.month),
        mw: 1,
      },
      {
        id: "reference-q4-26-cal-27",
        name: "Q4-26 + Cal-27",
        months: generated
          .filter((row) => row.month >= "2026-10" && row.month <= "2027-12")
          .map((row) => row.month),
        mw: 1,
      },
    ],
    layout: {
      ...DEFAULT_QUOTE_LAYOUT,
      columnOrder: [...DEFAULT_QUOTE_LAYOUT.columnOrder],
      hiddenColumns: [...DEFAULT_QUOTE_LAYOUT.hiddenColumns],
      columnWidths: {},
    },
  };
}

const boundedNumber = z.number().finite().min(-1_000_000_000).max(1_000_000_000);
const nullableBoundedNumber = boundedNumber.nullable();
const QuoteColumnIdSchema = z.enum(
  QUOTE_COLUMNS.map((column) => column.id) as [QuoteColumnId, ...QuoteColumnId[]]
);

const FxForwardPointSchema = z.object({
  deliveryMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  bid: z.number().finite().positive().nullable(),
  mid: z.number().finite().positive(),
  ask: z.number().finite().positive().nullable(),
}).strict().superRefine((point, ctx) => {
  if (point.bid != null && point.bid > point.mid) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["bid"], message: "bid cannot exceed mid" });
  }
  if (point.ask != null && point.ask < point.mid) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["ask"], message: "ask cannot be below mid" });
  }
});

const QuoteFxCurveSchema = z.object({
  pair: z.literal(EUR_RON_PAIR),
  curveId: z.string().uuid(),
  curveVersion: z.number().int().positive(),
  curveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  source: z.string().min(1).max(240),
  confirmedAt: z.string().datetime({ offset: true }).nullable(),
  appliedAt: z.string().datetime({ offset: true }),
  rateSide: z.literal("mid"),
  points: z.array(FxForwardPointSchema).min(1).max(240),
}).strict().superRefine((curve, ctx) => {
  const months = new Set<string>();
  curve.points.forEach((point, index) => {
    if (months.has(point.deliveryMonth)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["points", index, "deliveryMonth"], message: "delivery months must be unique" });
    }
    months.add(point.deliveryMonth);
  });
});

const QuoteSnapshotV1Schema = z.object({
  schemaVersion: z.literal(EEX_QUOTE_SCHEMA_VERSION),
  fxCurve: QuoteFxCurveSchema.nullable().optional().default(null),
  metadata: z.object({
    title: z.string().max(120),
    counterparty: z.string().max(120),
    notes: z.string().max(2000),
    asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    marketDataSource: z.string().max(120),
    inputsConfirmed: z.boolean(),
    validityMinutes: z.number().int().min(0).max(10_080),
  }).strict(),
  settings: z.object({
    ronBuyAdder: boundedNumber,
    ronSellAdder: boundedNumber,
    eurBuyAdder: boundedNumber,
    eurSellAdder: boundedNumber,
    defaultRisk: boundedNumber,
    priceDecimals: z.number().int().min(0).max(6),
    fxDecimals: z.number().int().min(0).max(8),
  }).strict(),
  rows: z
    .array(
      z.object({
        id: z.string().min(1).max(80),
        month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
        hours: z.number().int().min(1).max(1000),
        mw: nullableBoundedNumber,
        huPrice: nullableBoundedNumber,
        quotedSpread: nullableBoundedNumber,
        risk: nullableBoundedNumber,
        fx: nullableBoundedNumber,
      }).strict()
    )
    .min(1)
    .max(240),
  components: z
    .array(
      z.object({
        id: z.string().min(1).max(80),
        name: z.string().min(1).max(120),
        months: z.array(z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)).min(1).max(240),
        mw: boundedNumber,
      }).strict()
    )
    .max(100)
    .default([]),
  layout: z.object({
    columnOrder: z.array(QuoteColumnIdSchema).max(40),
    hiddenColumns: z.array(QuoteColumnIdSchema).max(40),
    columnWidths: z.record(z.coerce.number().min(56).max(420)),
    density: z.enum(["compact", "comfortable"]),
    showShapePnl: z.boolean(),
  }).strict(),
}).strict();

const QuoteMarketPolicySchema = z.enum(["bid", "ask", "mid", "last"]);

const QuoteMarketMarkSchema = z.object({
  acquisitionScopeId: z.string().uuid(),
  priceSourceScopeId: z.string().uuid().optional(),
  marketInstrumentId: z.string().uuid(),
  providerInstrumentKey: z.string().min(1).max(240),
  instrumentIsin: z.string().min(1).max(80).nullable(),
  bid: nullableBoundedNumber,
  ask: nullableBoundedNumber,
  mid: nullableBoundedNumber,
  last: nullableBoundedNumber,
  bidVolume: nullableBoundedNumber,
  askVolume: nullableBoundedNumber,
  lastVolume: nullableBoundedNumber,
  chosen: boundedNumber,
  marketTimestamp: z.string().datetime({ offset: true }),
  receivedAt: z.string().datetime({ offset: true }),
  freshnessAtApplication: z.literal("fresh"),
  coverageAtApplication: z.literal("quoted"),
}).strict().superRefine((mark, ctx) => {
  const expectedMid = mark.bid != null && mark.ask != null ? (mark.bid + mark.ask) / 2 : null;
  if ((expectedMid == null) !== (mark.mid == null) || (expectedMid != null && mark.mid != null && Math.abs(expectedMid - mark.mid) > 1e-9)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["mid"], message: "mid must match bid and ask" });
  }
});

const QuoteMarketApplicationRowSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  hu: QuoteMarketMarkSchema,
  ro: QuoteMarketMarkSchema.nullable(),
  observedBasis: nullableBoundedNumber,
  basisApplied: z.boolean(),
  huPriceManualOverride: z.boolean(),
  quotedSpreadManualOverride: z.boolean(),
}).strict();

const QuoteMarketApplicationSchema = z.object({
  companyId: z.string().uuid(),
  snapshotId: z.string().uuid(),
  provider: z.literal("EEX"),
  source: z.literal(EEX_QUOTE_MARKET_SOURCE),
  asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  selectedPolicy: QuoteMarketPolicySchema,
  basisApplied: z.boolean(),
  appliedAt: z.string().datetime({ offset: true }),
  signatureVersion: z.literal(1),
  signature: z.string().regex(/^[0-9a-f]{64}$/),
  rows: z.array(QuoteMarketApplicationRowSchema).min(1).max(240),
}).strict().superRefine((application, ctx) => {
  const months = new Set<string>();
  application.rows.forEach((row, index) => {
    if (months.has(row.month)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "month"], message: "months must be unique" });
    }
    months.add(row.month);
    if (row.basisApplied !== application.basisApplied) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "basisApplied"], message: "basis policy mismatch" });
    }
    const selectedHu = application.selectedPolicy === "mid"
      ? row.hu.mid
      : application.selectedPolicy === "last"
        ? row.hu.last
        : row.hu[application.selectedPolicy];
    if (selectedHu == null || Math.abs(selectedHu - row.hu.chosen) > 1e-9) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "hu", "chosen"], message: "chosen HU value must match the selected policy" });
    }
    if (row.ro) {
      const selectedRo = application.selectedPolicy === "mid"
        ? row.ro.mid
        : application.selectedPolicy === "last"
          ? row.ro.last
          : row.ro[application.selectedPolicy];
      if (selectedRo == null || Math.abs(selectedRo - row.ro.chosen) > 1e-9) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "ro", "chosen"], message: "chosen RO value must match the selected policy" });
      }
    }
    const expectedBasis = row.ro ? row.ro.chosen - row.hu.chosen : null;
    if ((expectedBasis == null) !== (row.observedBasis == null) || (expectedBasis != null && row.observedBasis != null && Math.abs(expectedBasis - row.observedBasis) > 1e-9)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "observedBasis"], message: "basis must equal RO minus HU" });
    }
    if (row.basisApplied && row.observedBasis == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "basisApplied"], message: "applied basis requires an RO mark" });
    }
    if (!row.basisApplied && row.quotedSpreadManualOverride) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rows", index, "quotedSpreadManualOverride"], message: "a non-applied basis cannot be marked as an override" });
    }
  });
});

const QuoteSnapshotV2Schema = QuoteSnapshotV1Schema
  .omit({ schemaVersion: true })
  .extend({
    schemaVersion: z.literal(2),
    marketSnapshot: QuoteMarketApplicationSchema,
  })
  .strict();

export const QuoteSnapshotSchema = z
  .discriminatedUnion("schemaVersion", [QuoteSnapshotV1Schema, QuoteSnapshotV2Schema])
  .superRefine((snapshot, ctx) => {
    if (snapshot.schemaVersion !== 2) return;
    if (snapshot.metadata.marketDataSource !== snapshot.marketSnapshot.source) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["metadata", "marketDataSource"], message: "market source is frozen" });
    }
    if (snapshot.metadata.asOf !== snapshot.marketSnapshot.asOf) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["metadata", "asOf"], message: "market as-of is frozen" });
    }
    // The immutable audit record may retain a month removed by a later manual
    // horizon edit; override markers make that divergence explicit.
  });

export function normalizeQuoteSnapshot(value: unknown): QuoteSnapshot | null {
  const parsed = QuoteSnapshotSchema.safeParse(value);
  if (!parsed.success) return null;
  const snapshot = parsed.data as QuoteSnapshot;
  const seen = new Set<QuoteColumnId>();
  const order = snapshot.layout.columnOrder.filter((id) => {
    if (!QUOTE_COLUMN_BY_ID.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  for (const column of QUOTE_COLUMNS) {
    if (!seen.has(column.id)) order.push(column.id);
  }
  const hidden = [...new Set(snapshot.layout.hiddenColumns)].filter((id) => id !== "month");
  const rows = [...snapshot.rows]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((row) => ({ ...row, id: row.id || row.month, hours: eexQuoteMonthHours(row.month) }));
  const componentIds = new Set<string>();
  const components = snapshot.components
    .filter((component) => {
      if (componentIds.has(component.id)) return false;
      componentIds.add(component.id);
      return true;
    })
    .map((component) => ({
      ...component,
      months: [...new Set(component.months)].sort(),
    }))
    .filter((component) => component.months.length > 0);
  return {
    ...snapshot,
    rows,
    components,
    layout: {
      ...snapshot.layout,
      columnOrder: order,
      hiddenColumns: hidden,
    },
  };
}

/**
 * Every economic edit invalidates trader confirmation. For schema-v2 quotes,
 * the signed EEX facts stay frozen while the two override flags are derived
 * from the current editable row values.
 */
export function resetQuoteConfirmationAfterEconomicEdit(
  snapshot: QuoteSnapshot
): QuoteSnapshot {
  if (snapshot.schemaVersion === 1) {
    return { ...snapshot, metadata: { ...snapshot.metadata, inputsConfirmed: false } };
  }
  const rowsByMonth = new Map(snapshot.rows.map((row) => [row.month, row] as const));
  return {
    ...snapshot,
    metadata: { ...snapshot.metadata, inputsConfirmed: false },
    marketSnapshot: {
      ...snapshot.marketSnapshot,
      rows: snapshot.marketSnapshot.rows.map((provenance) => {
        const row = rowsByMonth.get(provenance.month);
        return {
          ...provenance,
          huPriceManualOverride: row == null || !sameQuoteNumber(row.huPrice, provenance.hu.chosen),
          quotedSpreadManualOverride: provenance.basisApplied
            ? row == null || !sameQuoteNumber(row.quotedSpread, provenance.observedBasis)
            : false,
        };
      }),
    },
  };
}

export function quoteHasFrozenMarketSource(snapshot: QuoteSnapshot): snapshot is QuoteSnapshotV2 {
  return snapshot.schemaVersion === 2;
}

export function quoteLocalDraftKey(companyId: string | null, userId: string | null): string {
  return `cascena.eex-quotes.draft.v1.${companyId ?? "platform"}.${userId ?? "anonymous"}`;
}

export function quoteFxOverrideMonths(snapshot: QuoteSnapshot): string[] {
  if (!snapshot.fxCurve) return [];
  const centralByMonth = new Map(
    snapshot.fxCurve.points.map((point) => [point.deliveryMonth, point.mid])
  );
  return snapshot.rows.flatMap((row) => {
    const central = centralByMonth.get(row.month);
    if (central == null && row.fx == null) return [];
    if (central != null && row.fx != null && Math.abs(central - row.fx) <= 1e-9) return [];
    return [row.month];
  });
}

function sameQuoteNumber(left: number | null, right: number | null): boolean {
  if (left == null || right == null) return left === right;
  return Math.abs(left - right) <= 1e-9;
}
